/**
 * Enhanced Cart JavaScript
 * Handles quantity controls, cart updates, and AJAX functionality
 */

class EnhancedCart {
  constructor() {
    this.cartRoot = document.querySelector('.enhanced-cart');
    if (!this.cartRoot) {
      return;
    }

    this.deliveryDefaultText = null;
    this.deliverySuccessText = 'Gratulacje! Masz darmową dostawę';
    this.checkoutForm = null;
    this.checkoutSubmitButton = null;

    this.init();
  }

  init() {
    this.cacheDeliveryText();
    this.bindQuantityControls();
    this.bindAddToCartForms();
    this.bindRemoveButtons();
    this.bindCheckoutFlow();
    this.updateCartSummary();
    this.bindVatToggleLayoutUpdate();
    // Delay layout measurement until after browser paints the new DOM
    // (critical after AJAX section refresh which replaces the entire cart markup)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.updateRecommendationsLayout());
    });
  }

  /**
   * Cache default delivery text for later reuse
   */
  cacheDeliveryText() {
    const deliveryTextElement = this.cartRoot.querySelector('.delivery-text');
    if (deliveryTextElement) {
      this.deliveryDefaultText = deliveryTextElement.dataset.defaultText || deliveryTextElement.textContent.trim();
    }
  }

  /**
   * Bind quantity control buttons
   */
  bindQuantityControls() {
    const quantityButtons = this.cartRoot.querySelectorAll('.quantity-button');
    
    quantityButtons.forEach(button => {
      button.addEventListener('click', this.handleQuantityChange.bind(this));
    });
  }

  /**
   * Handle quantity increase/decrease
   */
  async handleQuantityChange(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const itemId = button.dataset.itemId;
    const action = button.dataset.action;
    const quantitySelector = button.closest('.quantity-selector');
    const quantityDisplay = quantitySelector.querySelector('.quantity-display');
    let currentQuantity = parseInt(quantityDisplay.textContent);
    
    // Calculate new quantity
    if (action === 'increase') {
      currentQuantity += 1;
    } else if (action === 'decrease' && currentQuantity > 1) {
      currentQuantity -= 1;
    } else {
      return; // Don't allow quantity to go below 1
    }

    // Disable buttons during update
    this.setQuantityButtonsState(quantitySelector, true);
    
    try {
      const response = await this.updateCartQuantity(itemId, currentQuantity);
      
      if (response.ok) {
        const updatedCart = await response.json();
        
        // Update the display
        quantityDisplay.textContent = currentQuantity;

        // Update line prices (both strikethrough and actual price)
        const cartItem = button.closest('.cart-item');
        const updatedItem = this.findCartItem(updatedCart.items, itemId);

        if (updatedItem && cartItem) {
          // Check if item has a discount (original > final)
          const hasDiscount = updatedItem.original_line_price > updatedItem.final_line_price;

          if (hasDiscount) {
            // Update strikethrough (original) price
            const originalPriceEl = cartItem.querySelector('.price-original');
            if (originalPriceEl) {
              originalPriceEl.textContent = this.formatMoney(updatedItem.original_line_price);
            }
            // Update discounted price
            const discountedPriceEl = cartItem.querySelector('.price-amount');
            if (discountedPriceEl) {
              discountedPriceEl.textContent = this.formatMoney(updatedItem.final_line_price);
            }
          } else {
            // No discount - just update the price
            const priceElement = cartItem.querySelector('.price-amount');
            if (priceElement) {
              priceElement.textContent = this.formatMoney(updatedItem.final_line_price || updatedItem.line_price || (updatedItem.price * updatedItem.quantity));
            }
          }
        }

        // Update cart summary (including discount amount)
        this.updateCartTotals(updatedCart);
        
        // VetApp integration disabled
        // document.dispatchEvent(new CustomEvent('cart:updated'));
        // document.dispatchEvent(new CustomEvent('ajaxCart:updated'));
        
      } else {
        throw new Error('Failed to update cart');
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      this.showNotification('Wystąpił błąd podczas aktualizacji koszyka', 'error');
    } finally {
      // Re-enable buttons
      this.setQuantityButtonsState(quantitySelector, false);
    }
  }

  /**
   * Update cart quantity via AJAX
   */
  async updateCartQuantity(itemId, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: itemId,
        quantity: quantity
      })
    });
  }

  /**
   * Find cart item by ID
   */
  findCartItem(items, itemId) {
    return items.find(item => item.id.toString() === itemId.toString());
  }

  /**
   * Set quantity buttons enabled/disabled state
   */
  setQuantityButtonsState(quantitySelector, disabled) {
    const buttons = quantitySelector.querySelectorAll('.quantity-button');
    buttons.forEach(button => {
      button.disabled = disabled;
      button.style.opacity = disabled ? '0.5' : '1';
    });
  }

  /**
   * Bind add to cart forms in recommendations
   */
  bindAddToCartForms() {
    const addToCartForms = this.cartRoot.querySelectorAll('.product-cart-tile__form');

    addToCartForms.forEach(form => {
      form.addEventListener('submit', this.handleAddToCart.bind(this));
    });

    const recommendationButtons = this.cartRoot.querySelectorAll('.add-to-cart-button[data-product-id]');

    recommendationButtons.forEach(button => {
      button.addEventListener('click', this.handleRecommendationAdd.bind(this));
    });
  }

  /**
   * Bind checkout flow
   */
  bindCheckoutFlow() {
    const form = this.cartRoot.querySelector('form.checkout-section');
    if (!form) {
      return;
    }

    this.checkoutForm = form;
    this.checkoutSubmitButton = form.querySelector('.checkout-button');
    // Form submits normally without availability checks
  }

  /**
   * Update recommendations layout based on cart item count.
   * With 3+ items on desktop, show full-width (all 4 tiles visible).
   * When the sidebar extends past the recs (e.g. VAT form expanded),
   * dynamic margin-top pushes the recs below the sidebar.
   */
  updateRecommendationsLayout() {
    const recsSection = this.cartRoot.querySelector('.recommendations-section');
    if (!recsSection) return;

    const grid = recsSection.querySelector('.recommendations-grid');

    // Reset dynamic push-down margin before measuring
    recsSection.style.marginTop = '';

    // On mobile/tablet, always use default scroll layout
    if (window.innerWidth < 1151) {
      recsSection.classList.remove('recommendations-section--full-width');
      if (grid) grid.classList.remove('recommendations-grid--wrap');
      return;
    }

    const cartItemCount = this.cartRoot.querySelectorAll('.cart-item').length;

    if (cartItemCount >= 3) {
      recsSection.classList.add('recommendations-section--full-width');
      if (grid) grid.classList.add('recommendations-grid--wrap');
      this.pushRecsBelow(recsSection);
    } else {
      recsSection.classList.remove('recommendations-section--full-width');
      if (grid) grid.classList.remove('recommendations-grid--wrap');
    }
  }

  /**
   * If the sticky sidebar's bottom edge extends below the recommendations'
   * top edge, add margin-top to push the recs row below the sidebar.
   */
  pushRecsBelow(recsSection) {
    const summary = this.cartRoot.querySelector('.cart-summary');
    if (!summary) return;

    // getBoundingClientRect gives actual rendered positions (accounts for sticky)
    const summaryRect = summary.getBoundingClientRect();
    const recsRect = recsSection.getBoundingClientRect();
    const overlap = summaryRect.bottom - recsRect.top;

    if (overlap > 0) {
      recsSection.style.marginTop = Math.ceil(overlap + 24) + 'px';
    }
  }

  /**
   * Re-run layout calculation when the VAT invoice toggle changes,
   * after the CSS transition completes (max-height 0.3s).
   */
  bindVatToggleLayoutUpdate() {
    const vatToggle = this.cartRoot.querySelector('[data-checkout-vat-toggle]');
    if (!vatToggle) return;

    vatToggle.addEventListener('change', () => {
      setTimeout(() => this.updateRecommendationsLayout(), 350);
    });
  }

  setCheckoutButtonState(button, disabled) {
    if (!button) {
      return;
    }

    button.disabled = Boolean(disabled);
    if (disabled) {
      button.classList.add('checkout-button--loading');
    } else {
      button.classList.remove('checkout-button--loading');
    }
  }

  /**
   * Handle add to cart submissions
   */
  async handleAddToCart(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const formData = new FormData(form);
    const button = form.querySelector('.product-cart-tile__add-btn');
    const originalText = button.querySelector('.product-cart-tile__add-text').textContent;
    
    // Disable button during request (no loading text change)
    button.disabled = true;
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: formData
      });
      
      if (response.ok) {
        const item = await response.json();

        // Show success message
        this.showNotification('Produkt dodany do koszyka!', 'success');

        // Update cart count if cart icon exists
        this.updateCartCount();

        await this.refreshCartSection();

        // Success UI: switch to Figma-styled "Dodano" pill briefly
        button.classList.add('add-to-cart--success');
        const iconContainer = button.querySelector('svg');
        const successIconUrl = button.getAttribute('data-success-icon');
        if (iconContainer) {
          const iconSrc = successIconUrl || `/assets/3589a8fde2a91501906cf41d590682ce1538c36e.svg`;
          iconContainer.outerHTML = `<img src="${iconSrc}" alt="" class="add-to-cart-success-icon" width="28" height="28">`;
        }
        button.querySelector('.product-cart-tile__add-text').textContent = 'Dodano';
        setTimeout(() => {
          button.classList.remove('add-to-cart--success');
          const successIcon = button.querySelector('.add-to-cart-success-icon');
          if (successIcon) {
            successIcon.outerHTML = `
              <svg class="product-cart-tile__cart-icon" width="29" height="28" viewBox="0 0 29 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M21.2269 24.0784H7.78688C6.54928 24.0784 5.54688 23.076 5.54688 21.8384V10.0784C5.54688 9.15108 6.29952 8.39844 7.22688 8.39844H21.7869C22.7142 8.39844 23.4669 9.15108 23.4669 10.0784V21.8384C23.4669 23.076 22.4645 24.0784 21.2269 24.0784Z" stroke="#2D4F40" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M18.9808 12.0847C18.8262 12.0847 18.7008 12.2101 18.7019 12.3647C18.7019 12.5192 18.8273 12.6447 18.9819 12.6447C19.1364 12.6447 19.2619 12.5192 19.2619 12.3647C19.2619 12.2101 19.1364 12.0847 18.9808 12.0847" stroke="#2D4F40" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10.0276 12.0847C9.87307 12.0847 9.74763 12.2101 9.74875 12.3647C9.74875 12.5192 9.87419 12.6447 10.0288 12.6447C10.1833 12.6447 10.3088 12.5192 10.3088 12.3647C10.3088 12.2101 10.1833 12.0847 10.0276 12.0847" stroke="#2D4F40" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10.0312 8.39406V8.11406V8.11406C10.0312 5.79454 11.9117 3.91406 14.2313 3.91406H14.7913C17.1108 3.91406 18.9913 5.79454 18.9913 8.11406V8.11406V8.39406" stroke="#2D4F40" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>`;
          }
          button.querySelector('.product-cart-tile__add-text').textContent = originalText;
        }, 1500);
        
        // VetApp integration disabled
        // document.dispatchEvent(new CustomEvent('cart:updated'));
        // document.dispatchEvent(new CustomEvent('ajaxCart:updated'));
        
        // NO RELOAD - Dynamic updates only
        // Removed window.location.reload() to prevent hot reload loop
        
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showNotification('Wystąpił błąd podczas dodawania produktu', 'error');
    } finally {
      // Re-enable button
      button.disabled = false;
      if (!button.classList.contains('add-to-cart--success')) {
        button.querySelector('.product-cart-tile__add-text').textContent = originalText;
      }
    }
  }

  /**
   * Handle add-to-cart via recommendation button
   */
  async handleRecommendationAdd(event) {
    event.preventDefault();

    const button = event.currentTarget;
    if (!button || button.disabled) {
      return;
    }

    const variantId = button.dataset.productId;
    if (!variantId) {
      console.warn('Brak ID produktu na przycisku dodawania do koszyka');
      return;
    }

    const quantity = parseInt(button.dataset.quantity || '1', 10) || 1;
    const textElement = button.querySelector('.add-to-cart-text');
    const originalText = textElement ? textElement.textContent : null;
    const iconElement = button.querySelector('.add-to-cart-icon, .add-to-cart-success-icon');
    const originalIconMarkup = iconElement ? iconElement.outerHTML : null;
    const successIconUrl = button.getAttribute('data-success-icon');

    button.disabled = true;
    button.classList.add('add-to-cart-button--loading');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    let sectionRefreshed = false;
    let revertTimeout = null;
    let reverted = false;

    const revertButtonState = () => {
      if (reverted || !button.isConnected) {
        reverted = true;
        return;
      }
      reverted = true;
      if (revertTimeout) {
        window.clearTimeout(revertTimeout);
        revertTimeout = null;
      }
      button.disabled = false;
      button.classList.remove('add-to-cart-button--loading', 'add-to-cart--success');
      if (textElement && originalText) {
        textElement.textContent = originalText;
      }
      const successIcon = button.querySelector('.add-to-cart-success-icon');
      if (successIcon && originalIconMarkup) {
        successIcon.outerHTML = originalIconMarkup;
      } else if (!successIcon && originalIconMarkup && !button.querySelector('.add-to-cart-icon')) {
        const contentWrapper = button.querySelector('.add-to-cart-content');
        if (contentWrapper) {
          contentWrapper.insertAdjacentHTML('afterbegin', originalIconMarkup);
        }
      }
    };

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to add recommendation item to cart');
      }

      await response.json();

      this.showNotification('Produkt dodany do koszyka!', 'success');
      await this.updateCartCount();

      button.classList.remove('add-to-cart-button--loading');
      button.classList.add('add-to-cart--success');

      const iconContainer = button.querySelector('.add-to-cart-icon, .add-to-cart-success-icon');
      if (iconContainer) {
        const iconSrc = successIconUrl || `/assets/3589a8fde2a91501906cf41d590682ce1538c36e.svg`;
        iconContainer.outerHTML = `<img src="${iconSrc}" alt="" class="add-to-cart-success-icon" width="28" height="28">`;
      }

      if (textElement) {
        textElement.textContent = 'Dodano';
      }

      const refreshDelay = 1800;
      const revertDelay = refreshDelay + 700;

      revertTimeout = window.setTimeout(revertButtonState, revertDelay);

      await new Promise(resolve => {
        window.setTimeout(resolve, refreshDelay);
      });

      sectionRefreshed = await this.refreshCartSection();
    } catch (error) {
      console.error('Error adding recommendation to cart:', error);
      this.showNotification('Wystąpił błąd podczas dodawania produktu', 'error');
      revertButtonState();
    } finally {
      if (!sectionRefreshed) {
        if (revertTimeout) {
          window.clearTimeout(revertTimeout);
        }
        revertButtonState();
      } else if (revertTimeout) {
        window.clearTimeout(revertTimeout);
      }
    }
  }

  /**
   * Bind remove buttons
   */
  bindRemoveButtons() {
    const removeButtons = this.cartRoot.querySelectorAll('.remove-button');
    
    removeButtons.forEach(button => {
      button.addEventListener('click', this.handleRemoveItem.bind(this));
    });
  }

  /**
   * Handle remove item via AJAX so we stay on the cart page
   */
  async handleRemoveItem(event) {
    event.preventDefault();

    const link = event.currentTarget;
    const cartItem = link.closest('.cart-item');
    if (!cartItem) {
      return;
    }

    const itemId = cartItem.dataset.itemId;
    if (!itemId) {
      console.warn('Cart item id missing for removal');
      return;
    }

    link.classList.add('remove-button--loading');
    link.setAttribute('aria-disabled', 'true');
    link.style.pointerEvents = 'none';

    try {
      const response = await this.updateCartQuantity(itemId, 0);

      if (!response.ok) {
        throw new Error('Failed to remove item from cart');
      }

      const updatedCart = await response.json();

      // Remove item from DOM
      cartItem.remove();

      // Update totals and counts
      this.updateCartTotals(updatedCart);

      if (Array.isArray(updatedCart.items) && updatedCart.items.length) {
        this.refreshLineItems(updatedCart.items);
      }

      if (updatedCart.item_count === 0) {
        this.showEmptyCartState();
      }

      // Recalculate recommendations layout after item removal
      requestAnimationFrame(() => this.updateRecommendationsLayout());

      this.showNotification('Produkt usunięty z koszyka', 'success');
    } catch (error) {
      console.error('Error removing cart item:', error);
      this.showNotification('Nie udało się usunąć produktu z koszyka', 'error');
    } finally {
      link.classList.remove('remove-button--loading');
      link.removeAttribute('aria-disabled');
      link.style.pointerEvents = '';
    }
  }

  /**
   * Refresh line prices for remaining items after a removal
   */
  refreshLineItems(cartItems) {
    cartItems.forEach(item => {
      const line = document.querySelector(`.cart-item[data-item-id="${item.id}"]`);
      if (!line) {
        return;
      }

      // Check if item has a discount
      const hasDiscount = item.original_line_price > item.final_line_price;

      if (hasDiscount) {
        // Update strikethrough (original) price
        const originalPriceEl = line.querySelector('.price-original');
        if (originalPriceEl) {
          originalPriceEl.textContent = this.formatMoney(item.original_line_price);
        }
        // Update discounted price
        const discountedPriceEl = line.querySelector('.price-amount');
        if (discountedPriceEl) {
          discountedPriceEl.textContent = this.formatMoney(item.final_line_price);
        }
      } else {
        // No discount - just update the price
        const priceElement = line.querySelector('.price-amount');
        if (priceElement) {
          priceElement.textContent = this.formatMoney(item.final_line_price || item.line_price || (item.price * item.quantity));
        }
      }

      const quantityDisplay = line.querySelector('.quantity-display');
      if (quantityDisplay) {
        quantityDisplay.textContent = item.quantity;
      }
    });
  }

  /**
   * Render empty cart UI when the last item is removed
   */
  showEmptyCartState() {
    if (!this.cartRoot) {
      return;
    }

    const container = this.cartRoot.querySelector('.enhanced-cart-container');
    if (!container) {
      return;
    }

    const cartContent = container.querySelector('.cart-content');
    if (!cartContent) {
      return;
    }

    const summary = container.querySelector('.cart-summary');
    if (summary) {
      summary.remove();
    }

    cartContent.innerHTML = '';

    const title = this.cartRoot.dataset.emptyCartTitle || 'Twój koszyk jest pusty';
    const message = this.cartRoot.dataset.emptyCartMessage || 'Dodaj produkty do koszyka, aby kontynuować zakupy.';
    const continueUrl = this.cartRoot.dataset.emptyCartContinueUrl || '/collections/all';
    const continueText = this.cartRoot.dataset.emptyCartContinueText || 'Kontynuuj zakupy';

    const header = document.createElement('h1');
    header.className = 'cart-header';
    header.textContent = title;
    cartContent.appendChild(header);

    const paragraph = document.createElement('p');
    paragraph.textContent = message;
    cartContent.appendChild(paragraph);

    const link = document.createElement('a');
    link.href = continueUrl;
    link.className = 'checkout-button';

    const span = document.createElement('span');
    span.className = 'checkout-button-text';
    span.textContent = continueText;

    link.appendChild(span);
    cartContent.appendChild(link);
  }

  /**
   * Update cart summary totals
   * Calculate from item prices to match collection page prices (German market price)
   */
  updateCartTotals(cart) {
    // Calculate subtotal from item prices * quantities for display (before discounts)
    let subtotal = 0;
    if (cart.items && Array.isArray(cart.items)) {
      cart.items.forEach(item => {
        const itemPrice = item.original_line_price || item.line_price || (item.price * item.quantity);
        subtotal += itemPrice;
      });
    } else {
      // Fallback to cart total if items array not available
      subtotal = typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price : cart.total_price;
    }

    const subtotalElement = this.cartRoot.querySelector('[data-cart-subtotal]');
    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(subtotal);
      subtotalElement.dataset.cartSubtotal = subtotal;
    }

    const freeShippingThreshold = this.getFreeShippingThreshold();
    let shippingCost = this.getShippingCost();

    if (freeShippingThreshold && subtotal >= freeShippingThreshold) {
      shippingCost = 0;
    }

    const shippingElement = this.cartRoot.querySelector('.cart-summary [data-shipping-cost]');
    if (shippingElement) {
      shippingElement.textContent = this.formatMoney(shippingCost);
      shippingElement.dataset.shippingCost = shippingCost;
    }

    // Use cart.total_price (includes discounts) for final total
    const discountedSubtotal = typeof cart.total_price === 'number' ? cart.total_price : subtotal;
    const totalWithShipping = discountedSubtotal + shippingCost;
    const originalTotalWithShipping = subtotal + shippingCost;
    const hasDiscount = subtotal > discountedSubtotal;

    // Update original price (strikethrough) element
    const originalPriceElement = this.cartRoot.querySelector('.original-total-price');
    if (originalPriceElement) {
      if (hasDiscount) {
        originalPriceElement.textContent = this.formatMoney(originalTotalWithShipping);
        originalPriceElement.dataset.originalTotal = originalTotalWithShipping;
        originalPriceElement.style.display = '';
      } else {
        originalPriceElement.style.display = 'none';
      }
    }

    // Update discounted total element
    const totalElement = this.cartRoot.querySelector('[data-cart-total-with-shipping]');
    if (totalElement) {
      totalElement.textContent = this.formatMoney(totalWithShipping);
      totalElement.dataset.cartTotalWithShipping = totalWithShipping;
      // Add/remove discounted class based on whether there's a discount
      if (hasDiscount) {
        totalElement.classList.add('discounted-price');
      } else {
        totalElement.classList.remove('discounted-price');
      }
    }

    // Update discount amount in summary
    const discountAmount = subtotal - discountedSubtotal;
    const discountValueElement = this.cartRoot.querySelector('.discount-value');
    const discountRow = this.cartRoot.querySelector('.discount-row');

    if (discountValueElement) {
      if (discountAmount > 0) {
        discountValueElement.textContent = '-' + this.formatMoney(discountAmount);
        if (discountRow) {
          discountRow.style.display = '';
        }
      } else {
        discountValueElement.textContent = '';
        if (discountRow) {
          discountRow.style.display = 'none';
        }
      }
    }

    this.updateFreeShippingThreshold(subtotal);
    this.updateCartRoot(cart);
    if (typeof cart.item_count === 'number' && !Number.isNaN(cart.item_count)) {
      this.updateCartCountDisplay(cart.item_count);
    }
  }

  /**
   * Persist latest cart totals on root element
   */
  updateCartRoot(cart) {
    if (this.cartRoot) {
      if (typeof cart.total_price === 'number' && !Number.isNaN(cart.total_price)) {
        this.cartRoot.dataset.cartTotal = cart.total_price;
      }
      if (typeof cart.item_count === 'number' && !Number.isNaN(cart.item_count)) {
        this.cartRoot.dataset.cartCount = cart.item_count;
      }
      // Update currency if available in cart response
      if (cart.currency && cart.currency.iso_code) {
        this.cartRoot.dataset.cartCurrency = cart.currency.iso_code;
      }
    }
  }

  /**
   * Get shipping cost from settings or default
   */
  getShippingCost() {
    if (this.cartRoot && this.cartRoot.dataset.baseShippingCost) {
      const parsedRoot = parseInt(this.cartRoot.dataset.baseShippingCost, 10);
      if (!Number.isNaN(parsedRoot)) {
        return parsedRoot;
      }
    }

    const shippingElement = this.cartRoot ? this.cartRoot.querySelector('.cart-summary [data-shipping-cost]') : null;
    if (shippingElement && shippingElement.dataset.shippingCost) {
      const parsedElement = parseInt(shippingElement.dataset.shippingCost, 10);
      if (!Number.isNaN(parsedElement)) {
        return parsedElement;
      }
    }

    // Default based on currency
    const currency = this.cartRoot?.dataset.cartCurrency || 'PLN';
    return currency === 'EUR' ? 499 : 1199; // 4.99 EUR or 11.99 PLN in cents
  }

  /**
   * Get configured free shipping threshold in cents
   */
  getFreeShippingThreshold() {
    if (this.cartRoot && this.cartRoot.dataset.freeShippingThreshold) {
      return parseInt(this.cartRoot.dataset.freeShippingThreshold, 10);
    }
    
    // Default based on currency
    const currency = this.cartRoot?.dataset.cartCurrency || 'PLN';
    return currency === 'EUR' ? 4900 : 14900; // 49.00 EUR or 149.00 PLN
  }

  /**
   * Update free shipping threshold display
   */
  updateFreeShippingThreshold(cartSubtotal) {
    const freeShippingThreshold = this.getFreeShippingThreshold();
    const remaining = Math.max(0, freeShippingThreshold - cartSubtotal);

    const amountElement = this.cartRoot.querySelector('[data-free-shipping-remaining]');
    const textElement = this.cartRoot.querySelector('.delivery-text');

    if (remaining > 0) {
      if (textElement) {
        const defaultText = textElement.dataset.defaultText || this.deliveryDefaultText || textElement.textContent.trim();
        textElement.textContent = defaultText;
      }
      if (amountElement) {
        amountElement.textContent = this.formatMoney(remaining);
        amountElement.style.display = '';
      }
    } else {
      if (textElement) {
        const successText = textElement.dataset.successText || this.deliverySuccessText;
        textElement.textContent = successText;
      }
      if (amountElement) {
        amountElement.textContent = '';
        amountElement.style.display = 'none';
      }
    }
  }

  /**
   * Update visible cart count elements (excluding the section wrapper)
   */
  updateCartCountDisplay(count) {
    const countElements = document.querySelectorAll('[data-cart-count]');
    countElements.forEach(element => {
      if (element.classList && element.classList.contains('enhanced-cart')) {
        return;
      }
      element.textContent = count;
    });
  }

  /**
   * Update cart count in header
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(element => {
        if (element.classList && element.classList.contains('enhanced-cart')) {
          return;
        }
        element.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Fetch latest section HTML and replace current markup
   */
  async refreshCartSection() {
    const cartRoot = document.querySelector('.enhanced-cart');
    if (!cartRoot) {
      return false;
    }

    const sectionId = cartRoot.dataset.sectionId;
    if (!sectionId) {
      return false;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionId);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch cart section: ${response.status}`);
      }

      const sectionHTML = await response.json();
      const html = sectionHTML[sectionId];

      if (!html) {
        throw new Error('Cart section HTML missing from response');
      }

      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      const newCart = wrapper.querySelector('.enhanced-cart');

      if (!newCart) {
        throw new Error('Cart markup missing in refreshed section');
      }

      cartRoot.replaceWith(newCart);

      // Re-initialise bindings on the fresh markup
      new EnhancedCart();

      return true;
    } catch (error) {
      console.error('Error refreshing cart section:', error);
      window.location.reload();
      return false;
    }
  }

  /**
   * Update cart summary on page load
   */
  updateCartSummary() {
    // This would typically fetch the current cart state
    // and update all relevant elements
    const cartData = this.getCartDataFromPage();
    if (cartData) {
      this.updateCartTotals({
        total_price: cartData.total_price,
        items_subtotal_price: cartData.items_subtotal_price,
        total_discount: cartData.total_discount,
        item_count: cartData.item_count
      });
    }
  }

  /**
   * Get cart data from the current page
  */
  getCartDataFromPage() {
    // Extract cart data from the rendered page
    const cartElement = this.cartRoot;
    if (cartElement) {
      const parsedTotal = parseInt(cartElement.dataset.cartTotal, 10);
      const parsedDiscountedTotal = parseInt(cartElement.dataset.cartTotalDiscounted, 10);
      const parsedDiscount = parseInt(cartElement.dataset.cartDiscount, 10);
      const parsedCount = cartElement.dataset.cartCount ? parseInt(cartElement.dataset.cartCount, 10) : null;
      return {
        // total_price should be the DISCOUNTED total (cart.total_price from Shopify)
        total_price: Number.isNaN(parsedDiscountedTotal) ? (Number.isNaN(parsedTotal) ? 0 : parsedTotal) : parsedDiscountedTotal,
        // items_subtotal_price is the original total before discounts
        items_subtotal_price: Number.isNaN(parsedTotal) ? 0 : parsedTotal,
        total_discount: Number.isNaN(parsedDiscount) ? 0 : parsedDiscount,
        item_count: Number.isNaN(parsedCount) ? null : parsedCount
      };
    }
    return null;
  }

  /**
   * Format money amount
   */
  formatMoney(cents) {
    const amount = cents / 100;
    
    // Get currency from cart root or default to PLN
    const currency = this.cartRoot?.dataset.cartCurrency || 'PLN';
    
    // Determine locale and currency symbol based on currency code
    let locale, currencySymbol;
    if (currency === 'EUR') {
      locale = 'de-DE'; // German locale for EUR formatting
      currencySymbol = '€';
      // Format with Intl.NumberFormat and move € to end
      const formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
      }).format(amount);
      // Move € from front to end: "€ 7,99" -> "7,99 €"
      return formatted.replace(/€\s*([\d.,]+)/, '$1 €');
    } else {
      locale = 'pl-PL';
      currencySymbol = 'zł';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
      }).format(amount).replace(currency, currencySymbol);
    }
  }

  /**
   * Show notification to user
   */
  showNotification(message, type = 'info') {
    if (type === 'error') {
      console.error('[EnhancedCart]', message);
    } else {
      console.debug('[EnhancedCart]', message);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  new EnhancedCart();
});

// Expose for external use
window.EnhancedCart = EnhancedCart;
